// ============================================================
// StatStrip - the forecast's key numbers, always on.
//
// The carousel earned its keep but ate the tab's lower half. The strip
// keeps the four numbers a decision actually needs - Hit %, expected
// damage, average attacks to kill, Crit % - plus a thumbnail of the
// hit distribution, and summons the full charts as an overlay on
// demand. Numbers always visible; ink on request.
// ============================================================
import { useState } from 'react';
import type { ProbabilityResult } from '../../engine/probability';
import ChartOverlay from './ChartOverlay';
import styles from '../../styles/StatStrip.module.css';

interface Props {
  result: ProbabilityResult | null;
  loading?: boolean;
}

const pct = (v: number) => `${Math.round(v * 100)}%`;

export default function StatStrip({ result, loading = false }: Props) {
  const [open, setOpen] = useState(false);

  if (!result) {
    return (
      <div className={styles.strip} data-testid="stat-strip">
        <span className={styles.empty}>{loading ? 'Computing…' : 'Select a weapon and target for a forecast.'}</span>
      </div>
    );
  }

  const bars = result.hits.probabilities;
  const maxBar = Math.max(...bars, 0.001);

  return (
    <>
      <button
        type="button"
        className={styles.strip}
        data-testid="stat-strip"
        onClick={() => setOpen(true)}
        title="The forecast at a glance - click for the full charts"
      >
        <span className={styles.stat}>
          <span className={styles.statLabel}>Hit</span>
          <span className={styles.statValue}>{pct(result.hits.hitChance)}</span>
        </span>
        <span className={styles.stat}>
          <span className={styles.statLabel}>E[dmg]</span>
          <span className={styles.statValue}>{result.damage.mean.toFixed(1)}</span>
        </span>
        <span className={styles.stat}>
          <span className={styles.statLabel}>Kill in</span>
          <span className={styles.statValue}>~{result.woundsToKill.meanAttacks.toFixed(1)}</span>
        </span>
        <span className={styles.stat}>
          <span className={styles.statLabel}>Crit</span>
          <span className={styles.statValue}>{pct(result.crits.anyCritChance)}</span>
        </span>
        {result.hits.jamChance > 0 && (
          <span className={styles.stat}>
            <span className={styles.statLabel}>Jam</span>
            <span className={styles.statValueBad}>{pct(result.hits.jamChance)}</span>
          </span>
        )}
        {/* Thumbnail: P(exactly N hits), the carousel's first slide in 40px. */}
        <span className={styles.spark} aria-hidden="true">
          {bars.map((p, i) => (
            <span key={i} className={styles.sparkBar} style={{ height: `${Math.max(8, (p / maxBar) * 100)}%` }} />
          ))}
        </span>
        <span className={styles.expand}>⤢</span>
      </button>
      {open && <ChartOverlay result={result} onClose={() => setOpen(false)} />}
    </>
  );
}
